import { listProgramsFn, listProgramsOptions } from "@/actions/program/handler";
import { getActiveUserProgramOptions } from "@/actions/user-program/handler";
import { CoachingApplicationDialog } from "@/components/coaching/CoachingApplicationDialog";
import { ProgramCard } from "@/components/program/ProgramCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { createWhatsAppUrl } from "@/lib/whatsapp";
import { seo } from "@/utils/seo";
import * as Sentry from "@sentry/tanstackstart-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  Dumbbell,
  KeyRound,
  Lock,
  MessageCircle,
  Unlock,
} from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import { z } from "zod";

const programSearchSchema = z.object({
  payment: z
    .enum(["success", "failed"])
    .catch(undefined as any)
    .optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute("/_app/program/")({
  validateSearch: (search) => programSearchSchema.parse(search),
  head: () => ({
    meta: seo({
      title: "Antrenman Programları | AtalayTheCoach",
      description:
        "Hedeflerinize yönelik profesyonel powerlifting ve güç antrenman programları. Programını seç ve gelişimini takip et.",
      canonical: "https://atalaythecoach.com/program",
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Antrenman Programları | AtalayTheCoach",
          description: "Profesyonel antrenman programları listesi.",
          url: "https://atalaythecoach.com/program",
        }),
      },
    ],
  }),
  component: ProgramPage,
});

function ProgramPage() {
  const search = Route.useSearch();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (search.payment === "success") {
      toast.success("Ödeme Başarılı!", {
        description: "Programınız hesabınıza tanımlandı. İyi antrenmanlar! 🎉",
      });
      queryClient.invalidateQueries({
        queryKey: getActiveUserProgramOptions().queryKey,
      });
    } else if (search.payment === "failed") {
      toast.error("Ödeme Başarısız", {
        description:
          "Ödeme işlemi tamamlanamadı. WhatsApp üzerinden destek alabilirsiniz.",
      });
    }
  }, [search.payment, queryClient]);

  return (
    <div className="container mx-auto max-w-4xl px-4 pt-24 pb-8">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-bold text-3xl text-foreground">
            Antrenman Programları
          </h1>
          <p className="mt-2 text-muted-foreground">
            Programını seç ve antrenmanlarını takip et
          </p>
        </div>
      </div>

      <ProgramList />
    </div>
  );
}

function ProgramList() {
  const {
    data: programsData,
    isLoading: isProgramsLoading,
    error: programsError,
  } = useQuery({
    ...listProgramsOptions(),
    queryFn: async () => {
      try {
        return await listProgramsFn();
      } catch (e) {
        Sentry.withScope((scope) => {
          scope.setTag("page", "/program");
          scope.setTag("query", "listPrograms");
          scope.setExtra("error", e);
          Sentry.captureException(
            e instanceof Error ? e : new Error(String(e)),
          );
        });
        throw e;
      }
    },
  });
  const { data: activeProgramData } = useQuery(getActiveUserProgramOptions());

  const [code, setCode] = React.useState("");
  const navigate = useNavigate();

  const programs = programsData?.body?.programs ?? [];
  const activeProgram =
    activeProgramData?.body && "programId" in activeProgramData.body
      ? activeProgramData.body
      : null;

  if (isProgramsLoading) {
    return <ProgramListSkeleton />;
  }

  if (programsError) {
    Sentry.withScope((scope) => {
      scope.setTag("page", "/program");
      scope.setTag("error_type", "programs_load_failed");
      scope.setExtra("programsError", programsError);
      Sentry.captureMessage(
        "Program listesi yuklenemedi - muhtemel auth/csrf hatasi",
        "warning",
      );
    });
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertTriangle className="mb-4 h-12 w-12 text-destructive" />
          <h3 className="mb-2 font-semibold text-lg">Programlar Yüklenemedi</h3>
          <p className="text-center text-muted-foreground">
            Programlar yüklenirken bir hata oluştu.
            <br />
            Lütfen sayfayı yenileyin veya tekrar giriş yapın.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => window.location.reload()}
          >
            Sayfayı Yenile
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (programs.length === 0) {
    return (
      <div className="space-y-8">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Dumbbell className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-2 font-semibold text-lg">Henüz Program Yok</h3>
            <p className="text-center text-muted-foreground">
              Aktif antrenman programı bulunmuyor.
              <br />
              Yakında yeni programlar eklenecek!
            </p>
          </CardContent>
        </Card>
        <ProgramGuidanceCta />
      </div>
    );
  }

  const activeId = activeProgram?.programId;

  // Sort active program to top within each list
  const sortActive = (list: typeof programs) =>
    [...list].sort((a, b) => {
      if (a.programId === activeId) return -1;
      if (b.programId === activeId) return 1;
      return 0;
    });

  const isPaidProgramCheck = (p: (typeof programs)[number]) =>
    Boolean(
      p.requiresPurchase ||
        p.isPaid ||
        (p.price && p.price > 0) ||
        p.accessCode,
    );

  // Free programs
  const freePrograms = sortActive(
    programs.filter((p) => !isPaidProgramCheck(p)),
  );

  // Paid programs
  const paidPrograms = sortActive(
    programs.filter((p) => isPaidProgramCheck(p)),
  );

  const handleJoinWithCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    navigate({
      to: "/join/$code",
      params: { code: code.trim().toUpperCase() },
    });
  };

  return (
    <div className="space-y-8">
      <Tabs defaultValue="free" className="w-full">
        <div className="-mx-4 sticky top-0 z-30 mb-6 bg-background/95 px-4 py-4 backdrop-blur-sm md:mx-0 md:px-0">
          <TabsList className="flex h-auto w-full flex-wrap justify-center gap-2 border-none bg-transparent p-0 shadow-none">
            <TabsTrigger
              value="free"
              className="shrink-0 cursor-pointer rounded-xl border border-border bg-background px-4 py-2.5 font-bold text-muted-foreground text-xs transition-all duration-200 hover:bg-muted hover:text-foreground active:scale-[0.98] data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md sm:text-sm dark:border-border dark:bg-background dark:text-muted-foreground dark:data-[state=active]:border-primary dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground dark:data-[state=active]:shadow-md dark:hover:bg-muted dark:hover:text-foreground"
            >
              <span className="hidden sm:inline">Ücretsiz Programlar</span>
              <span className="sm:hidden">Ücretsiz</span>
            </TabsTrigger>
            <TabsTrigger
              value="paid"
              className="shrink-0 cursor-pointer rounded-xl border border-border bg-background px-4 py-2.5 font-bold text-muted-foreground text-xs transition-all duration-200 hover:bg-muted hover:text-foreground active:scale-[0.98] data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md sm:text-sm dark:border-border dark:bg-background dark:text-muted-foreground dark:data-[state=active]:border-primary dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground dark:data-[state=active]:shadow-md dark:hover:bg-muted dark:hover:text-foreground"
            >
              <span className="hidden sm:inline">Ücretli Programlar</span>
              <span className="sm:hidden">Ücretli</span>
            </TabsTrigger>
            <TabsTrigger
              value="code"
              className="shrink-0 cursor-pointer rounded-xl border border-border bg-background px-4 py-2.5 font-bold text-muted-foreground text-xs transition-all duration-200 hover:bg-muted hover:text-foreground active:scale-[0.98] data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md sm:text-sm dark:border-border dark:bg-background dark:text-muted-foreground dark:data-[state=active]:border-primary dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground dark:data-[state=active]:shadow-md dark:hover:bg-muted dark:hover:text-foreground"
            >
              Kodla Katıl
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="free" className="mt-2 outline-none">
          {freePrograms.length === 0 ? (
            <Card className="border-border/40 bg-card/30">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <Unlock className="mb-4 h-10 w-10 text-muted-foreground/40" />
                <h3 className="mb-1 font-bold text-lg">
                  Hazır Ücretsiz Program Bulunmuyor
                </h3>
                <p className="text-muted-foreground text-sm">
                  Şu anda listelenecek ücretsiz hazır program bulunmamaktadır.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-8 md:grid-cols-2">
              {freePrograms.map((program) => (
                <ProgramCard
                  key={program.programId}
                  program={program}
                  isActive={program.programId === activeId}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="paid" className="mt-2 outline-none">
          {paidPrograms.length === 0 ? (
            <Card className="border-border/40 bg-card/30">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <Lock className="mb-4 h-10 w-10 text-muted-foreground/40" />
                <h3 className="mb-1 font-bold text-lg">
                  Hazır Ücretli Program Bulunmuyor
                </h3>
                <p className="text-muted-foreground text-sm">
                  Şu anda listelenecek ücretli hazır program bulunmamaktadır.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-8 md:grid-cols-2">
              {paidPrograms.map((program) => (
                <ProgramCard
                  key={program.programId}
                  program={program}
                  isActive={program.programId === activeId}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="code" className="mt-2 outline-none">
          <Card className="relative mx-auto max-w-md overflow-hidden rounded-3xl border border-primary/10 bg-gradient-to-b from-card to-accent/20 shadow-primary/5 shadow-xl">
            {/* Subtle glow elements */}
            <div className="-right-12 -top-12 pointer-events-none absolute h-32 w-32 rounded-full bg-primary/5 blur-3xl" />
            <div className="-left-12 -bottom-12 pointer-events-none absolute h-32 w-32 rounded-full bg-primary/5 blur-3xl" />

            <CardContent className="relative z-10 p-8">
              <div className="space-y-4 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary shadow-inner transition-all duration-300 hover:rotate-6 hover:scale-110">
                  <KeyRound className="h-8 w-8" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="font-bold text-foreground text-xl tracking-tight">
                    Özel Programa Katıl
                  </h3>
                  <p className="text-muted-foreground/80 text-sm leading-relaxed">
                    Koçunuz veya katıldığınız grup tarafından size iletilen 6
                    haneli özel erişim kodunu girerek programınıza anında
                    başlayabilirsiniz.
                  </p>
                </div>
              </div>

              <form
                onSubmit={handleJoinWithCodeSubmit}
                className="mt-8 space-y-6"
              >
                <div className="space-y-3">
                  <label
                    htmlFor="tab-access-code"
                    className="ml-1 block font-bold text-muted-foreground text-xs uppercase tracking-wider"
                  >
                    Erişim Kodu
                  </label>
                  <input
                    id="tab-access-code"
                    placeholder="ÖRN: JSUAYX"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className={cn(
                      "flex h-14 w-full rounded-2xl border-2 border-muted bg-background/50 text-center font-bold font-mono text-2xl uppercase tracking-[0.5em] shadow-sm transition-all placeholder:text-muted-foreground/30 placeholder:tracking-normal focus:bg-background focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/10",
                      code.length === 6 && "border-primary/50 bg-background",
                    )}
                    maxLength={6}
                  />
                </div>
                <Button
                  type="submit"
                  className="h-12 w-full rounded-2xl bg-primary font-bold text-primary-foreground shadow-md shadow-primary/10 transition-all hover:bg-primary/95 hover:shadow-lg hover:shadow-primary/15 active:scale-[0.98]"
                  disabled={code.length < 6}
                >
                  Programa Git ve Başla
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      {!activeProgram && <ProgramGuidanceCta />}
    </div>
  );
}

function ProgramGuidanceCta() {
  return (
    <section className="rounded-2xl border border-primary/20 bg-primary/5 p-6 text-center sm:p-8">
      <p className="mb-2 font-semibold text-primary text-sm">
        Hangi programın size uygun olduğundan emin değil misiniz?
      </p>
      <h2 className="font-bold text-2xl tracking-tight">
        Hedefinize uygun başlangıç noktasını birlikte belirleyelim
      </h2>
      <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
        Seviyenizi, antrenman geçmişinizi ve hedefinizi değerlendirerek doğru
        programı seçmenize yardımcı olalım.
      </p>
      <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
        <CoachingApplicationDialog packageTitle="Program Seçimi Desteği">
          <Button size="lg" data-analytics-cta="program_guidance_coaching">
            Koçluk Başvurusu Yap
          </Button>
        </CoachingApplicationDialog>
        <Button asChild size="lg" variant="outline">
          <a
            href={createWhatsAppUrl(
              "Merhaba, hedefime uygun antrenman programını seçmek için bilgi almak istiyorum.",
            )}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MessageCircle className="mr-2 h-4 w-4" />
            WhatsApp'tan Sor
          </a>
        </Button>
      </div>
    </section>
  );
}

function ProgramListSkeleton() {
  return (
    <div className="grid gap-8 md:grid-cols-2">
      {[1, 2].map((i) => (
        <div
          key={i}
          className="flex flex-col overflow-hidden rounded-2xl border border-transparent bg-muted/10"
        >
          <div className="aspect-video w-full overflow-hidden">
            <Skeleton className="h-full w-full bg-muted/20" />
          </div>
          <div className="flex flex-1 flex-col p-6">
            <div className="mb-6 flex-1 space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-8 w-3/4 bg-muted/20" />
                <Skeleton className="h-4 w-1/2 bg-muted/20" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-full bg-muted/20" />
                <Skeleton className="h-4 w-5/6 bg-muted/20" />
              </div>
              <div className="flex items-center gap-4">
                <Skeleton className="h-4 w-20 bg-muted/20" />
                <Skeleton className="h-4 w-20 bg-muted/20" />
              </div>
            </div>
            <div className="flex gap-2">
              <Skeleton className="h-11 flex-1 rounded-xl bg-muted/20" />
              <Skeleton className="h-11 flex-1 rounded-xl bg-muted/20" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
